import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { trackPixel } from "@/lib/pixel";
import { toast } from "sonner";
import { Loader2, Check, Truck, ShieldCheck, CreditCard, ArrowLeft } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import KitBuilder from "@/components/KitBuilder";
import KitVideo from "@/components/KitVideo";
import { SHIPPING_DETAILS, STANDARD_RETURN_POLICY } from "@/lib/merchantSchema";

const SITE_URL = "https://untamedsportsgear.co.za";

function sizeAvail(item, size) {
  // Single source of truth = the product's own per-size inventory. Only fall back to
  // the aggregate stock when the product has NO per-size tracking at all.
  const sbs = item.stock_by_size || {};
  if (sbs && Object.keys(sbs).length > 0) return sbs[size] ?? 0;
  return item.stock ?? 0;
}

function sizeInStock(item, size) {
  return sizeAvail(item, size) > 0;
}

export default function BundleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addBundle } = useCart();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selections, setSelections] = useState({});
  const [mmSlots, setMmSlots] = useState([]); // Mix & Match: [{product_id, size}, ...]
  const viewed = useRef(false);

  const isMix = bundle?.bundle_type === "mix_match";
  const isKit = bundle?.bundle_type === "kit";

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/bundles/${slug}`)
      .then((r) => {
        if (!active) return;
        const b = r.data;
        setBundle(b);
        if (b.bundle_type === "mix_match") {
          // Default each of the N slots to a sensible product (distinct if duplicates off)
          const pool = b.eligible_products || [];
          const slots = [];
          for (let i = 0; i < (b.choose_quantity || 2); i++) {
            const prod = b.allow_duplicates ? pool[0] : pool[i % pool.length];
            const firstSize = prod ? (prod.sizes || []).find((s) => sizeInStock(prod, s)) || (prod.sizes || [])[0] || "One Size" : "";
            slots.push({ product_id: prod?.product_id || "", size: firstSize });
          }
          setMmSlots(slots);
        } else {
          // Fixed: default a size per PHYSICAL unit (a 2× item gets 2 independent defaults)
          const defaults = {};
          for (const it of b.items) {
            const firstInStock = (it.sizes || []).find((s) => sizeInStock(it, s));
            const def = firstInStock || (it.sizes || [])[0] || "One Size";
            for (let u = 0; u < (it.quantity || 1); u++) defaults[`${it.product_id}#${u}`] = def;
          }
          setSelections(defaults);
        }
      })
      .catch((e) => {
        if (!active) return;
        if (e.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e.response?.data?.detail));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  // ViewContent — fire once per bundle view
  useEffect(() => {
    if (bundle && !viewed.current) {
      viewed.current = true;
      const ids = bundle.bundle_type === "mix_match"
        ? (bundle.eligible_products || []).map((p) => p.product_id)
        : bundle.bundle_type === "kit"
        ? (bundle.kit_groups || []).flatMap((g) => (g.products || []).map((p) => p.product_id))
        : (bundle.items || []).map((i) => i.product_id);
      trackPixel("ViewContent", {
        content_ids: ids,
        content_name: bundle.name,
        content_type: "product",
        value: bundle.bundle_price,
        currency: "ZAR",
      });
    }
  }, [bundle]);

  const jsonLd = useMemo(() => {
    if (!bundle) return null;
    const priceValid = new Date();
    priceValid.setFullYear(priceValid.getFullYear() + 1);
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: bundle.name,
      description: bundle.description || bundle.headline,
      image: bundle.image_url ? [bundle.image_url.startsWith("http") ? bundle.image_url : `${SITE_URL}${bundle.image_url}`] : undefined,
      brand: { "@type": "Brand", name: "Untamed Gear" },
      offers: {
        "@type": "Offer",
        url: `${SITE_URL}/bundles/${bundle.slug}`,
        priceCurrency: "ZAR",
        price: bundle.bundle_price,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        priceValidUntil: priceValid.toISOString().split("T")[0],
        ...(bundle.created_at ? { validFrom: bundle.created_at } : {}),
        hasMerchantReturnPolicy: STANDARD_RETURN_POLICY,
        shippingDetails: SHIPPING_DETAILS,
      },
    };
  }, [bundle]);

  // ---- Mix & Match derived helpers ----
  const mmProduct = (pid) => (bundle?.eligible_products || []).find((p) => p.product_id === pid);
  const mmNormalTotal = mmSlots.reduce((s, sl) => s + (mmProduct(sl.product_id)?.price || 0), 0);
  const mmSaving = bundle ? Math.max(0, mmNormalTotal - bundle.bundle_price) : 0;
  const mmComplete = (() => {
    if (!isMix || mmSlots.length !== (bundle?.choose_quantity || 0)) return false;
    // every slot has a product + an in-stock size
    if (!mmSlots.every((sl) => sl.product_id && sl.size && sizeInStock(mmProduct(sl.product_id) || {}, sl.size))) return false;
    // duplicate-aware: total picks of each exact product+size must not exceed its available units
    const need = {};
    for (const sl of mmSlots) need[`${sl.product_id}#${sl.size}`] = (need[`${sl.product_id}#${sl.size}`] || 0) + 1;
    return Object.entries(need).every(([key, qty]) => {
      const [pid, size] = key.split("#");
      return sizeAvail(mmProduct(pid) || {}, size) >= qty;
    });
  })();

  const setSlot = (idx, patch) => setMmSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const handleAddMix = () => {
    if (!mmComplete) { toast.error(`Please choose ${bundle.choose_quantity} products with a valid in-stock size.`); return; }
    if (!bundle.allow_duplicates) {
      const ids = mmSlots.map((s) => s.product_id);
      if (new Set(ids).size !== ids.length) { toast.error("This deal doesn't allow the same product twice."); return; }
    }
    const need = {};
    for (const s of mmSlots) need[`${s.product_id}#${s.size}`] = (need[`${s.product_id}#${s.size}`] || 0) + 1;
    for (const key of Object.keys(need)) {
      const [pid, size] = key.split("#");
      const p = mmProduct(pid);
      const avail = (p?.stock_by_size && Object.keys(p.stock_by_size).length) ? (p.stock_by_size[size] ?? 0) : (p?.stock ?? 0);
      if (avail < need[key]) { toast.error(`Only ${Math.max(0, avail)} of ${p?.name} (${size}) in stock — you picked ${need[key]}.`); return; }
    }
    const units = mmSlots.map((s) => {
      const p = mmProduct(s.product_id);
      return { product_id: p.product_id, name: p.name, image_url: p.image_url, size: s.size, normal: p.price };
    });
    addBundle(bundle, units);
    toast.success("Bundle added to cart!");
    navigate("/cart");
  };

  const handleAddBundle = () => {
    const units = [];
    for (const it of bundle.items) {
      const qty = it.quantity || 1;
      const needsSize = it.allow_size_selection && (it.sizes || []).length > 1;
      for (let u = 0; u < qty; u++) {
        const chosen = selections[`${it.product_id}#${u}`];
        if (needsSize && !chosen) { toast.error(`Please pick a size for ${it.name}${qty > 1 ? ` (item ${u + 1})` : ""}`); return; }
        if (chosen && !sizeInStock(it, chosen)) { toast.error(`${it.name} (${chosen}) is out of stock — pick another size`); return; }
        units.push({
          product_id: it.product_id, name: it.name, image_url: it.image_url,
          size: chosen || (it.sizes || [])[0] || "One Size", normal: it.price,
        });
      }
    }
    addBundle(bundle, units);
    toast.success("Bundle added to cart!");
    navigate("/cart");
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-[#F7931E]" /></div>;
  }

  if (notFound || !bundle) {
    return (
      <div data-testid="bundle-not-found" className="max-w-lg mx-auto px-4 py-24 text-center">
        <SEOHead title="Bundle Not Found" canonicalPath={`/bundles/${slug}`} noindex />
        <h1 className="text-2xl font-bold uppercase text-white font-heading mb-4">Bundle Not Found</h1>
        <p className="text-[#A3A3A3] mb-8">This deal may have ended.</p>
        <Link to="/deals" className="bg-[#F7931E] text-black font-bold uppercase tracking-wide py-3 px-8 hover:bg-[#D97715] transition-colors">
          See Live Deals
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="bundle-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEOHead
        title={`${bundle.name} — Bundle Deal`}
        description={bundle.headline || bundle.description || `Save R${Math.round(bundle.saving ?? bundle.saving_up_to ?? 0)} on the ${bundle.name} bundle from Untamed Gear.`}
        canonicalPath={`/bundles/${bundle.slug}`}
        image={bundle.image_url}
        type="product"
        jsonLd={jsonLd}
      />

      <Link to="/deals" className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-white text-xs font-bold uppercase tracking-wider mb-6 transition-colors">
        <ArrowLeft size={14} /> All Deals
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Media — Complete Kit shows the brand reel; other bundles keep the image */}
        {isKit ? (
          <div className="relative">
            <KitVideo
              srcMp4={`${process.env.PUBLIC_URL || ""}/media/untamed-kit-reel.mp4`}
              srcWebm={`${process.env.PUBLIC_URL || ""}/media/untamed-kit-reel.webm`}
              poster={bundle.image_url || bundle.kit_groups?.[0]?.products?.[0]?.image_url || ""}
            />
            {(bundle.saving > 0 || bundle.saving_up_to > 0) && (
              <span className="absolute top-4 left-4 z-10 bg-[#F7931E] text-black text-sm font-black uppercase tracking-wider px-4 py-1.5">
                Save R{Math.round(bundle.saving_up_to)}
              </span>
            )}
          </div>
        ) : (
          <div className="relative aspect-square bg-[#171717] border border-[#262626] overflow-hidden">
            {bundle.image_url ? (
              <img src={bundle.image_url} alt={bundle.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#525252] font-heading uppercase">No image</div>
            )}
            {(bundle.saving > 0 || bundle.saving_up_to > 0) && (
              <span className="absolute top-4 left-4 bg-[#F7931E] text-black text-sm font-black uppercase tracking-wider px-4 py-1.5">
                Save {isMix && bundle.min_normal_total !== bundle.max_normal_total ? "up to " : ""}R{Math.round(isMix ? bundle.saving_up_to : bundle.saving)}
              </span>
            )}
          </div>
        )}

        {/* Details */}
        <div>
          {bundle.headline && <p className="text-[#F7931E] text-sm font-bold uppercase tracking-[0.25em] mb-2">{bundle.headline}</p>}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white font-heading leading-none">
            {bundle.name}
          </h1>
          {bundle.description && <p className="text-[#A3A3A3] mt-4 text-sm sm:text-base">{bundle.description}</p>}

          {isKit && <div className="mt-6"><KitBuilder bundle={bundle} /></div>}

          {!isKit && (<>
          {/* Price block */}
          <div className="flex items-end gap-4 mt-6">
            <span data-testid="bundle-price" className="text-[#F7931E] font-black text-4xl">R{bundle.bundle_price.toFixed(0)}</span>
            {isMix ? (
              mmNormalTotal > bundle.bundle_price && (
                <div className="mb-1">
                  <span className="text-[#737373] line-through text-lg" data-testid="mm-normal-total">R{mmNormalTotal.toFixed(0)}</span>
                  <span className="ml-3 text-green-400 font-bold text-sm" data-testid="mm-saving">You save R{mmSaving.toFixed(0)}</span>
                </div>
              )
            ) : (
              bundle.normal_total > bundle.bundle_price && (
                <div className="mb-1">
                  <span className="text-[#737373] line-through text-lg">R{bundle.normal_total.toFixed(0)}</span>
                  <span className="ml-3 text-green-400 font-bold text-sm">You save R{bundle.saving.toFixed(0)} ({bundle.saving_pct}%)</span>
                </div>
              )
            )}
          </div>

          {isMix ? (
            /* ===== MIX & MATCH: choose N from the eligible pool ===== */
            <div className="mt-8">
              <h2 className="text-white font-bold uppercase tracking-tight text-lg font-heading mb-1">Choose your {bundle.choose_quantity}</h2>
              <p className="text-[#A3A3A3] text-xs mb-4">
                Pick any {bundle.choose_quantity} from {bundle.eligible_products.length} option{bundle.eligible_products.length !== 1 ? "s" : ""}
                {bundle.allow_duplicates ? " — the same one twice is allowed." : " — each choice must be different."}
              </p>
              <div className="space-y-4">
                {mmSlots.map((slot, idx) => {
                  const chosen = mmProduct(slot.product_id);
                  // products available for THIS slot (respect duplicates rule)
                  const takenElsewhere = bundle.allow_duplicates ? new Set() : new Set(mmSlots.filter((_, i) => i !== idx).map((s) => s.product_id));
                  const options = bundle.eligible_products.filter((p) => p.product_id === slot.product_id || !takenElsewhere.has(p.product_id));
                  return (
                    <div key={idx} data-testid={`mm-slot-${idx}`} className="bg-[#171717] border border-[#262626] p-4">
                      <p className="text-[#F7931E] text-[10px] font-black uppercase tracking-wider mb-2">Selection {idx + 1}</p>
                      {/* product picker */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {options.map((p) => {
                          const selected = slot.product_id === p.product_id;
                          return (
                            <button
                              key={p.product_id}
                              data-testid={`mm-product-${idx}-${p.product_id}`}
                              onClick={() => {
                                const firstSize = (p.sizes || []).find((s) => sizeInStock(p, s)) || (p.sizes || [])[0] || "One Size";
                                setSlot(idx, { product_id: p.product_id, size: firstSize });
                              }}
                              className={`px-3 py-1.5 text-xs font-bold border transition-colors ${
                                selected ? "bg-[#F7931E] text-black border-[#F7931E]" : "bg-black text-white border-[#262626] hover:border-[#F7931E]"
                              }`}
                            >
                              {p.name} <span className="opacity-70">R{p.price.toFixed(0)}</span>
                            </button>
                          );
                        })}
                      </div>
                      {/* size picker for the chosen product */}
                      {chosen && (
                        <div>
                          <p className="text-[#A3A3A3] text-[10px] font-bold uppercase tracking-wider mb-2">Size</p>
                          <div className="flex flex-wrap gap-2">
                            {(chosen.sizes || []).map((sz) => {
                              const avail = sizeAvail(chosen, sz);
                              // units of this exact product+size already claimed by OTHER slots
                              const usedByOthers = mmSlots.filter((s, i) => i !== idx && s.product_id === chosen.product_id && s.size === sz).length;
                              const inStock = avail - usedByOthers > 0;
                              const selected = slot.size === sz;
                              return (
                                <button
                                  key={sz}
                                  data-testid={`mm-size-${idx}-${sz}`}
                                  disabled={!inStock}
                                  onClick={() => inStock && setSlot(idx, { size: sz })}
                                  title={!inStock ? (avail <= 0 ? "Out of stock" : "No more units available") : ""}
                                  className={`min-w-[42px] px-3 py-1.5 text-xs font-bold border transition-colors ${
                                    selected ? "bg-[#F7931E] text-black border-[#F7931E]"
                                    : inStock ? "bg-black text-white border-[#262626] hover:border-[#F7931E]"
                                    : "bg-black text-[#525252] border-[#1a1a1a] line-through cursor-not-allowed"
                                  }`}
                                >
                                  {sz}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                data-testid="add-bundle-to-cart-btn"
                onClick={handleAddMix}
                disabled={!mmComplete}
                className="w-full mt-8 bg-[#F7931E] text-black font-black uppercase tracking-wide py-4 hover:bg-[#D97715] transition-colors btn-press text-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add {bundle.choose_quantity === 2 ? "Both" : `All ${bundle.choose_quantity}`} to Cart — R{bundle.bundle_price.toFixed(0)}
              </button>
            </div>
          ) : (
            /* ===== FIXED bundle ===== */
            <>
            <div className="mt-8">
              <h2 className="text-white font-bold uppercase tracking-tight text-lg font-heading mb-4">What's Included</h2>
              <div className="space-y-4">
                {bundle.items.map((it) => {
                  const showSizes = it.allow_size_selection && (it.sizes || []).length > 1;
                  const qty = it.quantity || 1;
                  return (
                    <div key={it.product_id} data-testid={`bundle-item-${it.product_id}`} className="bg-[#171717] border border-[#262626] p-4">
                      <div className="flex items-center gap-3">
                        {it.image_url && <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover bg-[#0A0A0A] shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-bold text-sm truncate">{qty > 1 ? `${qty}× ` : ""}{it.name}</p>
                          <p className="text-[#A3A3A3] text-xs">Retail R{(it.price * qty).toFixed(0)}</p>
                        </div>
                      </div>
                      {showSizes ? (
                        <div className="mt-3 space-y-3">
                          {Array.from({ length: qty }).map((_, u) => (
                            <div key={u}>
                              <p className="text-[#A3A3A3] text-[10px] font-bold uppercase tracking-wider mb-2">
                                {qty > 1 ? `Item ${u + 1} — choose size` : "Choose size"}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {it.sizes.map((sz) => {
                                  const inStock = sizeInStock(it, sz);
                                  const selected = selections[`${it.product_id}#${u}`] === sz;
                                  return (
                                    <button
                                      key={sz}
                                      data-testid={`bundle-size-${it.product_id}-${u}-${sz}`}
                                      disabled={!inStock}
                                      onClick={() => setSelections((p) => ({ ...p, [`${it.product_id}#${u}`]: sz }))}
                                      className={`min-w-[42px] px-3 py-1.5 text-xs font-bold border transition-colors ${
                                        selected ? "bg-[#F7931E] text-black border-[#F7931E]"
                                        : inStock ? "bg-black text-white border-[#262626] hover:border-[#F7931E]"
                                        : "bg-black text-[#525252] border-[#1a1a1a] line-through cursor-not-allowed"
                                      }`}
                                    >
                                      {sz}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        (it.sizes || []).length === 1 && (
                          <p className="mt-2 text-[#A3A3A3] text-xs">Size: <span className="text-white font-bold">{it.sizes[0]}</span>{qty > 1 ? ` ×${qty}` : ""}</p>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="add-bundle-to-cart-btn"
              onClick={handleAddBundle}
              className="w-full mt-8 bg-[#F7931E] text-black font-black uppercase tracking-wide py-4 hover:bg-[#D97715] transition-colors btn-press text-lg"
            >
              Add Complete Bundle to Cart — R{bundle.bundle_price.toFixed(0)}
            </button>
            </>
          )}
          </>
          )}

          {/* Trust / delivery */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <div className="flex items-center gap-2 text-[#A3A3A3] text-xs">
              <Truck size={16} className="text-[#F7931E] shrink-0" /> National delivery via PUDO. Free over R1,000.
            </div>
            <div className="flex items-center gap-2 text-[#A3A3A3] text-xs">
              <CreditCard size={16} className="text-[#F7931E] shrink-0" /> Secure card payment via Yoco.
            </div>
            <div className="flex items-center gap-2 text-[#A3A3A3] text-xs">
              <ShieldCheck size={16} className="text-[#F7931E] shrink-0" /> 7-day returns on unworn gear.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
